// Fachada da camada de sync — ponto único que o local-db chama ao gravar.
// Plan: docs/plan/sync-conta-cross-device.md (S3).
//
// O local-db (fonte da verdade local) chama enqueueWrite/enqueueDelete DEPOIS de
// gravar no IDB. Aqui decompomos o id local em (providerCode, profileId, itemKey),
// enfileiramos no outbox e agendamos o flush debounced. Tudo BEST-EFFORT: se não
// há sessão Swipe ou o scope não é sincronizável (lista direta / perfil _main),
// vira no-op — o app funciona 100% offline/local sem conta.

import { orFallbackAfter } from '../lib/deadline'
import { markSwipeLinked } from '../swipe-linked-flag'

import type { WriteCapture } from './capture'
import { isAcceptableClock } from './clock'
import {
  flushNow as flushNowInternal,
  resetProvisionedCache as resetProvisionedCacheInternal,
  scheduleFlush,
} from './flusher'
import { clearOutbox as clearOutboxInternal, enqueue, type OutboxOrigin } from './outbox'
import { clearPullCursors as clearPullCursorsInternal } from './puller'
import {
  clearSwipeSession as clearSwipeSessionInternal,
  extractSwipeUserId as extractSwipeUserIdInternal,
  getSessionGeneration as getSessionGenerationInternal,
  getSwipeUserId,
  hasSwipeSession as hasSwipeSessionInternal,
  setSwipeSessionFromPairing as setSwipeSessionFromPairingInternal,
} from './swipe-session'
import { deleteRemoteProfile, upsertRemoteProfile } from './sync-api'
import { decomposeLocalId } from './sync-scope'
import type { SyncKind } from './types'

/**
 * Enfileira um WRITE (upsert) de item de sync a partir do id LOCAL completo.
 * `payload` é o objeto local serializável (PlaybackPosition, Favorite, etc).
 *
 * O relógio do LWW é o MOMENTO DA ESCRITA em milissegundos (`Date.now()`), não o
 * `lastWatchedAt` do conteúdo (que fica no payload como dado). Por quê: o backend
 * descarta updates com `updatedAt <= gravado` (estrito); usar segundos faria 2
 * saves do MESMO item no mesmo segundo colidirem (o 2º seria ignorado, perdendo
 * progresso). Ms + "agora" garante monotonia por escrita. No-op se não sincronizável.
 */
export async function enqueueWrite(args: {
  localId: string
  kind: SyncKind
  payload: unknown
  /** O dono e o relógio lidos na ENTRADA da escrita (sync/capture.ts). Sem ela, lê agora. */
  capture?: WriteCapture
}): Promise<void> {
  // O dono é o da CAPTURA (a entrada da escrita, antes dos `await` do IDB); sem ela, a
  // conta de agora, lida no mesmo tick da conferência de sessão. Lido depois dos `await`,
  // uma troca de conta no meio trocava o dono (ver "O DONO" em outbox.ts).
  const owner = args.capture ? args.capture.owner : getSwipeUserId()
  if (owner === null) return
  const d = decomposeLocalId(args.localId)
  if (!d) return
  await enqueue({
    owner,
    providerCode: d.providerCode,
    profileId: d.profileId,
    kind: args.kind,
    itemKey: d.itemKey,
    payload: args.payload,
    updatedAt: new Date(args.capture ? args.capture.at : Date.now()).toISOString(),
    deleted: false,
  })
  scheduleFlush()
}

/**
 * Desfecho de um `enqueueDelete`:
 * - `queued`: o tombstone está no outbox (vai pro servidor no próximo flush);
 * - `no-session`: sem conta Swipe AGORA, ou (com `asUser`) a conta de agora não é a
 *   pedida — nada foi enfileirado; pode valer tentar de novo quando houver a sessão;
 * - `not-syncable`: o scope não sincroniza (lista direta / perfil `_main`) — não há
 *   cópia no servidor pra apagar;
 * - `superseded`: já havia no outbox um pendente MAIS NOVO do mesmo item; ele fica e
 *   é ele que vai (o tombstone perde, como deve perder pra uma escrita posterior);
 * - `failed`: o outbox DESCARTOU o write (quota/IDB). O servidor não vai saber;
 * - `bad-clock`: (só `enqueueUpsert`) o `updatedAt` explícito está fora do que o
 *   cliente aceita (antes de `SYNC_CLOCK_MIN_MS` ou depois de agora). Nada foi
 *   enfileirado. Ver sync/clock.ts.
 */
export type TombstoneStatus = 'queued' | 'superseded' | 'no-session' | 'not-syncable' | 'failed' | 'bad-clock'

/**
 * Enfileira um DELETE (tombstone) de item de sync a partir do id LOCAL.
 * Relógio do LWW = momento da exclusão em ms (mesma razão do enqueueWrite).
 * Devolve o desfecho (ver `TombstoneStatus`); os callers antigos o ignoram.
 *
 * `updatedAt` explícito: pra um tombstone que apaga dado ANTIGO (a migração de
 * carimbos) sem vencer escrita legítima feita DEPOIS em outro aparelho. Um
 * tombstone com o relógio de "agora" ganha no LWW de tudo que o servidor tem —
 * inclusive do que a TV ainda não puxou. Default = agora (comportamento de sempre).
 */
export async function enqueueDelete(args: {
  localId: string
  kind: SyncKind
  updatedAt?: string
  /** Rastreia o desfecho do push na telemetria (ver `OutboxEntry.origin`). */
  origin?: OutboxOrigin
  /** Só enfileira se a conta de agora for esta (quem decidiu pelo dono ANTES de um
   *  `await`, como a migração dos carimbos). */
  asUser?: string
  /** Ver `enqueueWrite`. */
  capture?: WriteCapture
}): Promise<TombstoneStatus> {
  const owner = args.capture ? args.capture.owner : getSwipeUserId()
  if (owner === null || (args.asUser !== undefined && owner !== args.asUser)) return 'no-session'
  const d = decomposeLocalId(args.localId)
  if (!d) return 'not-syncable'
  const ok: TombstoneStatus = await enqueue({
    owner,
    providerCode: d.providerCode,
    profileId: d.profileId,
    kind: args.kind,
    itemKey: d.itemKey,
    payload: null,
    updatedAt: args.updatedAt ?? new Date(args.capture ? args.capture.at : Date.now()).toISOString(),
    deleted: true,
    ...(args.origin !== undefined ? { origin: args.origin } : {}),
  })
  scheduleFlush()
  return ok
}

/**
 * Enfileira uma ESCRITA (upsert) de item de sync a partir do id LOCAL, com o desfecho
 * (mesmos valores do `enqueueDelete`) e o relógio LWW explícito. Existe pra quem
 * precisa saber se a escrita entrou e com qual relógio: a migração de carimbos reescreve
 * o `series_progress` recalculado (ele é DERIVADO: o tombstone ancorado no carimbo
 * perdia no LWW pro derivado contaminado recalculado depois). O `enqueueWrite` de sempre
 * segue sem desfecho, com o relógio de agora.
 *
 * O relógio explícito só entra se estiver entre `SYNC_CLOCK_MIN_MS` e agora
 * (`isAcceptableClock`); fora disso, `bad-clock` sem enfileirar. Antes ele ia cru: um
 * boot com o relógio de 1970 (TV sem RTC antes do NTP) mandava um derivado que perdia
 * calado no LWW, e um com o relógio de 2037 travava a linha no servidor contra toda
 * escrita legítima até lá (sonda W1 do review, 29/09/2026).
 */
export async function enqueueUpsert(args: {
  localId: string
  kind: SyncKind
  payload: unknown
  updatedAt?: string
  origin?: OutboxOrigin
  /** Ver `enqueueDelete`. */
  asUser?: string
}): Promise<TombstoneStatus> {
  const owner = getSwipeUserId()
  if (owner === null || (args.asUser !== undefined && owner !== args.asUser)) return 'no-session'
  const d = decomposeLocalId(args.localId)
  if (!d) return 'not-syncable'
  if (args.updatedAt !== undefined && !isAcceptableClock(args.updatedAt)) return 'bad-clock'
  const ok: TombstoneStatus = await enqueue({
    owner,
    providerCode: d.providerCode,
    profileId: d.profileId,
    kind: args.kind,
    itemKey: d.itemKey,
    payload: args.payload,
    updatedAt: args.updatedAt ?? new Date().toISOString(),
    deleted: false,
    ...(args.origin !== undefined ? { origin: args.origin } : {}),
  })
  scheduleFlush()
  return ok
}

/**
 * Sincroniza um PERFIL (upsert) — chamado quando o perfil é criado/editado
 * localmente. Perfis são entidade própria no servidor (swipe_profiles), vão
 * DIRETO pro endpoint (não pro outbox: são raros e pequenos). É o que PROVISIONA
 * o perfil remoto, sem o qual o push de itens daquele perfil tomaria 403. Se
 * falhar (offline), o lazy-provision do flusher recupera no próximo push.
 * No-op se não há sessão Swipe.
 *
 * `capture` (sync/capture.ts): o pedido só sai com o token da conta que fez a ação.
 * Se a conta trocou entre a ação e o pedido, ele não sai (o lazy-provision do flush da
 * conta dona provisiona depois); senão o perfil criado por A ia pra conta de B.
 */
export async function enqueueProfileUpsert(args: {
  id: string
  name: string
  avatar?: string | null
  kind?: 'adult' | 'kids'
  providerCode?: string | null
  updatedAtSec?: number
  capture?: WriteCapture
}): Promise<void> {
  const owner = args.capture ? args.capture.owner : getSwipeUserId()
  if (owner === null) return
  await upsertRemoteProfile(
    args.id,
    {
      name: args.name,
      ...(args.avatar !== undefined ? { avatar: args.avatar } : {}),
      ...(args.kind !== undefined ? { kind: args.kind } : {}),
      ...(args.providerCode !== undefined ? { providerCode: args.providerCode } : {}),
      updatedAt: new Date((args.updatedAtSec ?? Math.floor(Date.now() / 1000)) * 1000).toISOString(),
    },
    { asUser: owner },
  )
}

/** Sincroniza a EXCLUSÃO de um perfil (tombstone). No-op se sem sessão Swipe. Ver
 *  `enqueueProfileUpsert` sobre a `capture`. */
export async function enqueueProfileDelete(
  id: string,
  updatedAtSec?: number,
  capture?: WriteCapture,
): Promise<void> {
  const owner = capture ? capture.owner : getSwipeUserId()
  if (owner === null) return
  const sec = updatedAtSec ?? Math.floor(Date.now() / 1000)
  await deleteRemoteProfile(id, new Date(sec * 1000).toISOString(), { asUser: owner })
}

export { bindFlushEvents, flushNow, resetProvisionedCache } from './flusher'
export {
  bindPullEvents,
  ensureProfilesPulled,
  profilesPulledThisSession,
  reportSyncContextMissing,
  setSyncAppliedHandler,
  syncPull,
} from './puller'
export type { ProfilesPullOutcome, SyncPullResult } from './puller'
export { listQuarantined } from './outbox'
export {
  clearSwipeSession,
  getSwipeUserId,
  hasSwipeSession,
  onSwipeIdentityChange,
  setSwipeSession,
  setSwipeSessionFromPairing,
} from './swipe-session'
export { clearOutbox, pendingCount } from './outbox'
export {
  confirmPinRecoverRemote,
  removeProfilePinRemote,
  requestPinRecoverRemote,
  setProfilePinRemote,
  verifyProfilePinRemote,
} from './sync-api'
export type { ConfirmPinRecoverResult, SetPinResult, VerifyPinResult } from './sync-api'

/**
 * Liga a sessão Swipe a partir do pareamento aprovado. Se a conta Swipe MUDOU
 * (outro swipeUserId), tenta mandar o pendente da conta que sai com o token dela e zera
 * os cursores ANTES; o cache de provisionamento zera em qualquer login. O pendente da
 * conta anterior não vaza pra a nova (o flush só manda o do dono da sessão) e não se
 * perde (fica no outbox com o dono dele). Retorna false se o token não tiver
 * swipeUserId (não liga). Ponto canônico do login Swipe.
 */
/** Teto do flush da conta que sai, na troca direta de conta. O mesmo do flush final do
 *  sign-out (`FLUSH_TIMEOUT_MS`, sign-out.ts): suficiente pro caso normal e curto o
 *  bastante pra não parecer travamento no sofá. */
export const SWITCH_FLUSH_TIMEOUT_MS = 3000

export async function loginSwipeSession(accessToken: string, refreshToken: string): Promise<boolean> {
  const previous = getSwipeUserId()
  const nextId = extractSwipeUserIdInternal(accessToken)
  if (nextId && previous && previous !== nextId) {
    // Troca DIRETA de conta, com a sessão da que sai viva: o token dela ainda vale
    // AGORA. Última chance de o pendente dela subir pra conta dela (review de
    // 29/09/2026, sonda Q-B): antes a troca apagava esse pendente, e ele nunca chegava.
    // Com prazo por corrida (o flush pode pendurar; o pareamento é uma espera visível):
    // se estourar, a troca segue e o flush em voo para no pedido seguinte (a conta
    // mudou, ver o `request` do flusher).
    await orFallbackAfter(
      flushNowInternal().then(
        () => undefined,
        () => undefined,
      ),
      SWITCH_FLUSH_TIMEOUT_MS,
      undefined,
    )
    // O que não saiu FICA, com o dono dele, e sai quando aquela conta voltar (o flush
    // só manda o do dono da sessão: nada vaza). Nada do outbox é apagado na troca.
    clearPullCursorsInternal()
  }
  // Caches de provisionamento e de perfis RECUSADOS (403 profile_not_owned) valem só
  // pra conta que os mediu: zera em QUALQUER login, não só na troca entre duas contas
  // conhecidas. Sem isso, com a sessão de A morta (previous = null) e B entrando no
  // mesmo run, o perfil do próprio B — recusado quando o token era o de A — ficava
  // pulado até o app reiniciar (gate de cybersec, 27/09/2026).
  resetProvisionedCacheInternal()
  const persisted = setSwipeSessionFromPairingInternal(accessToken, refreshToken)
  if (persisted) {
    // Marca este aparelho como "já vinculou uma conta Swipe" (durável, UX-only).
    // Se a sessão cair depois (401 recuperável limpa a sessão MAS não o marcador),
    // o gate mostra a variante "reconecte" no lugar do gate frio. NÃO é
    // identidade nem authz — só decide a copy. Só quando persistiu de fato: um
    // pareamento que não durou no disco (quota) não deixa marcador órfão.
    markSwipeLinked()
  }
  return persisted
}

/**
 * Encerra a conta Swipe e TODO o estado de sync (sessão + outbox + cache de
 * perfis provisionados). Ponto ÚNICO de limpeza pra TODOS os caminhos de
 * logout/troca de conta/expiração — evita vazamento entre contas. Faz um flush
 * final ANTES de limpar (não perde o pendente), se houver sessão, com o mesmo teto por
 * corrida da troca de conta (`SWITCH_FLUSH_TIMEOUT_MS`; o `sign-out.ts` da TV faz o
 * dele antes, com o mesmo número).
 *
 * Só limpa a sessão que COMEÇOU o sign-out (AP-116, sonda R5 do reviewer). O
 * `MobileRoot` chama isto sem esperar e volta pro login; com o flush final pendurado,
 * outra sessão entrava (outra conta, ou a mesma pareada de novo) e o sign-out, ao
 * retomar, apagava a sessão e o pendente dela. A pergunta é de SESSÃO (a geração), não de
 * conta: o re-login da mesma conta também é afetado. Se a sessão só MORREU no meio (o
 * refresh tomou 401) e ninguém entrou, a limpeza segue.
 */
export async function endSwipeSync(opts: { flushFirst?: boolean } = {}): Promise<void> {
  const generation = getSessionGenerationInternal()
  if (opts.flushFirst && getSwipeUserId() !== null) {
    await orFallbackAfter(
      flushNowInternal().then(
        () => undefined,
        () => undefined,
      ),
      SWITCH_FLUSH_TIMEOUT_MS,
      undefined,
    )
    if (getSessionGenerationInternal() !== generation && hasSwipeSessionInternal()) return
  }
  clearSwipeSessionInternal()
  resetProvisionedCacheInternal()
  clearPullCursorsInternal()
  await clearOutboxInternal()
}
